import { useEffect, useMemo, useState } from 'react';
import { config } from '@accounts/config';
import { Again, Factors, type Link } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Provider, Refusal } from '@accounts/transport';
import { Button, Callout, Loading } from '@beyond-js/ui/react';
import { Problem, Wordmark } from '@accounts/ui';

/**
 * `/signin/again`: the window a page opens when it needs to know it is still the same person and the account has no
 * password or second factor to ask for (`Again`, E8). The person signs in again with one of the account's own
 * providers; the provider returns here, and the provider's token goes once to `POST
 * /v1/sessions/current/reauthentication`, which renews this browser's session only for this account's own
 * identity and a recent authentication. The window then tells the page that asked, on this origin's channel and for
 * the attempt in its address, and closes; that page goes on with what the person was doing.
 *
 * It offers only the providers linked to this account. A sign-in with another account, or one the service does not
 * accept, is said here and told to the page as a failure; nothing is renewed and nothing is sent again.
 */
export function SignInAgain() {
	const { text, client, accounts } = usePresence();
	const factors = useMemo(() => new Factors(client), [client]);
	const [returned] = useState(() => Provider.returned());
	const [linked, setLinked] = useState<string[]>();
	const [busy, setBusy] = useState(Boolean(returned.code || returned.token));
	const [failure, setFailure] = useState<unknown>();
	const [proved, setProved] = useState(false);

	// Back from the provider: the code is exchanged in this tab, and the token is used once, here
	useEffect(() => {
		if (returned.error) {
			setFailure(new Refusal('UNAUTHENTICATED', text('again.refused')));
			Again.tell('failed');
			return;
		}
		if (!returned.code && !returned.token) return;
		const token = returned.token ? Promise.resolve(returned.token) : accounts.exchange(returned.code as string);
		token
			.then(value => factors.reauthenticate('provider', value))
			.then(() => {
				setProved(true);
				Again.tell('proved');
				globalThis.close();
			})
			.catch(error => {
				setFailure(error);
				Again.tell('failed');
			})
			.finally(() => setBusy(false));
		// Once per page load: the provider's return is read once
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// What the account can sign in with, among what this installation offers
	useEffect(() => {
		if (returned.code || returned.token) return;
		Promise.all([accounts.account(), accounts.providers()])
			.then(([own, providers]) => {
				const held = new Set(own.identities.map((link: Link) => link.provider));
				setLinked((providers.providers ?? []).filter(name => name !== 'email' && held.has(name)));
			})
			.catch(error => setFailure(error));
	}, [accounts, returned]);

	function start(name: string) {
		setBusy(true);
		const provider = new Provider(config.provider, config.key);
		void provider
			.authorize(name, Again.back())
			.then(where => {
				globalThis.location.href = where;
			})
			.catch(error => {
				setFailure(error);
				setBusy(false);
			});
	}

	return (
		<div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-4 py-10">
			<header className="flex flex-col gap-3">
				<Wordmark label="Accounts" />
				<div>
					<h1 className="text-title-1 text-ink">{text('again.title')}</h1>
					<p className="mt-1 text-body text-ink-muted">{text('again.lead')}</p>
				</div>
			</header>
			{proved ? <Callout tone="success" live title={text('again.done')} /> : null}
			{failure ? <Problem error={failure} title={text('again.failed')} /> : null}
			{busy ? <Loading label={text('again.working')} /> : null}
			{!busy && !proved && !returned.code && !returned.token ? (
				<div className="flex flex-col gap-2">
					{linked === undefined && !failure ? <Loading label={text('again.reading')} /> : null}
					{(linked ?? []).map(name => (
						<Button key={name} variant="secondary" onClick={() => start(name)}>
							{text('signin.with', { provider: text(`identities.provider.${name}`) })}
						</Button>
					))}
					{linked?.length === 0 ? <Callout tone="warning" live title={text('again.none')} /> : null}
				</div>
			) : null}
			{!proved ? (
				<Button variant="quiet" onClick={() => globalThis.close()}>
					{text('again.cancel')}
				</Button>
			) : null}
		</div>
	);
}
